import { Box, useToast } from '@chakra-ui/react';
import { Button, Icon } from '../../ui';
import { useLayout } from '../../hooks/useLayout';
import AppDrawer from '../../components/AppDrawer';
import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SessionContext from '../../contexts/SessionContext';
import { useCreateInventoryItem } from '../../hooks/inventory/useCreateInventoryItem';
import { useInventoryQuickAddPreview } from '../../hooks/inventory/useInventoryQuickAddPreview';
import { useGetInventoryUnits } from '../../hooks/inventory/useGetInventoryUnits';
import { useSearchSuppliers } from '../../hooks/useSearchSuppliers';
import {
    InventoryBin,
    InventoryCategory,
    CreateInventoryItemInput,
    InventoryFormReferenceField,
    InventoryTag,
} from '../../types/inventory';
import {
    parseInventoryDuplicateError,
    parseInventoryPartType,
    parseInventoryReferenceValidationError,
    referenceValidationFieldToFormKey,
    coerceInventoryUnit,
} from '../../utils/inventoryConstants';
import type { TypeaheadOption } from '../../components/inventory/InventoryTypeaheadField';
import InventoryItemImageField from '../../components/inventory/InventoryItemImageField';
import { useUploadInventoryImage } from '../../hooks/inventory/useUploadInventoryImage';
import { useCreateInventoryTag } from '../../hooks/inventory/useCreateInventoryTag';
import { useCreateInventoryBin } from '../../hooks/inventory/useCreateInventoryBin';
import { filterLocations, LocationEntry } from '../../hooks/useGetLocations';
import { formatPartNumber } from '../../functions/formatPartNumber';
import ConfirmMakeTagModal from './ConfirmMakeTagModal';
import { appendTagIfMissing, normalizeTagNameKey, tagListHasName } from '../../utils/inventoryTags';
import IdentifyStep from './addItem/IdentifyStep';
import LookingUpStep from './addItem/LookingUpStep';
import PartSummaryCard from './addItem/PartSummaryCard';
import ItemDetailsForm from './addItem/ItemDetailsForm';
import RequiredFieldsStatus from './addItem/RequiredFieldsStatus';
import type { TabbedSectionsHandle } from '../../ui';
import {
    IDENTIFY_FIELDS,
    emptyAddItemForm,
    focusAddItemField,
    type AddItemDetailsVariant,
    type AddItemFormState,
    type AddItemSectionId,
    type AddItemStep,
    type PrefillField,
    type RequiredFieldKey,
} from './addItem/types';

/** Lookup-filled values tracked for badges and reset on a new lookup. */
type PrefillKey = PrefillField | 'additionalPartNumbers';

const PREFILL_FORM_FIELDS: ReadonlySet<keyof AddItemFormState> = new Set<PrefillField>([
    'name',
    'partType',
    'unit',
    'unitCost',
    'supplierId',
]);

interface AddInventoryItemDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    locations: LocationEntry[];
    defaultLocationId?: string;
    onViewExistingItem: (inventoryItemId: string) => void;
    /** Opens the inventory import upload → mapping → preview flow. */
    onImport?: () => void;
}

const AddInventoryItemDrawer = ({
    isOpen,
    onClose,
    locations,
    defaultLocationId = '',
    onViewExistingItem,
    onImport,
}: AddInventoryItemDrawerProps) => {
    const toast = useToast();
    const layout = useLayout();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const organizationName =
        sessionContext?.isAuthenticated === true
            ? sessionContext.organizationName
            : null;
    const abortRef = useRef<AbortController | null>(null);
    const [previewMatchItemId, setPreviewMatchItemId] = useState<string | null>(null);
    const [compositeDuplicateItemId, setCompositeDuplicateItemId] = useState<string | null>(
        null,
    );
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<InventoryFormReferenceField, string>>
    >({});
    const [form, setForm] = useState<AddItemFormState>(() => emptyAddItemForm(defaultLocationId));
    const [additionalPartNumbers, setAdditionalPartNumbers] = useState<Array<string>>([]);
    const [manualModeEntered, setManualModeEntered] = useState(false);
    const [lookupStarted, setLookupStarted] = useState(false);
    const [prefilled, setPrefilled] = useState<ReadonlySet<PrefillKey>>(() => new Set());
    /** Make tag the lookup appended (so a later lookup can take it back out). */
    const [autoTaggedMake, setAutoTaggedMake] = useState<string | null>(null);
    const [lastCommittedMfgPartNumber, setLastCommittedMfgPartNumber] = useState('');
    const [lastCommittedCategory, setLastCommittedCategory] = useState<InventoryCategory | ''>('');
    const [supplierSearch, setSupplierSearch] = useState('');
    const [tags, setTags] = useState<Array<InventoryTag>>([]);
    const [suggestedMake, setSuggestedMake] = useState<string | null>(null);
    const [makeTagModalOpen, setMakeTagModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [bins, setBins] = useState<Array<InventoryBin>>([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageKey, setImageKey] = useState<string | undefined>(undefined);
    const sectionsRef = useRef<TabbedSectionsHandle<AddItemSectionId>>(null);
    const uploadImageMutation = useUploadInventoryImage();
    const createTagMutation = useCreateInventoryTag();
    const createBinMutation = useCreateInventoryBin();

    const { data: allSuppliersData, isLoading: isSupplierLoading } = useSearchSuppliers('', {
        listAll: isOpen,
    });

    const {
        data: unitsData,
        isLoading: isUnitsLoading,
        isError: isUnitsError,
    } = useGetInventoryUnits(isOpen);

    const allowedUnits = unitsData?.units ?? [];

    const supplierOptions: TypeaheadOption[] = useMemo(() => {
        const all = allSuppliersData?.suppliers ?? [];
        const q = supplierSearch.trim().toLowerCase();
        return all
            .filter((s) => !q || s.title.toLowerCase().includes(q))
            .map((s) => ({ value: s._id, label: s.title }));
    }, [allSuppliersData?.suppliers, supplierSearch]);

    const supplierDisplay = useMemo(() => {
        const match = supplierOptions.find((o) => o.value === form.supplierId);
        if (match) return match.label;
        return supplierSearch;
    }, [supplierOptions, form.supplierId, supplierSearch]);

    const selectableLocations = useMemo(
        () => filterLocations(locations, { excludeAll: true }),
        [locations],
    );

    const locationOptions: TypeaheadOption[] = useMemo(
        () => selectableLocations.map((loc) => ({ value: loc._id, label: loc.title })),
        [selectableLocations],
    );

    const handleCreateError = useCallback((error: unknown) => {
        const referenceError = parseInventoryReferenceValidationError(error);
        if (referenceError) {
            const formKey = referenceValidationFieldToFormKey(referenceError.field);
            setFieldErrors({ [formKey]: referenceError.message });
            return;
        }
        const duplicate = parseInventoryDuplicateError(error);
        if (duplicate) {
            setCompositeDuplicateItemId(duplicate.existingItemId);
            return;
        }
        const message = (error as { response?: { data?: { message?: string; }; }; })?.response
            ?.data?.message;
        toast({
            title: 'Failed to add part',
            description: message ?? 'Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }, [toast]);

    const createMutation = useCreateInventoryItem({
        onError: handleCreateError,
    });

    const previewMutation = useInventoryQuickAddPreview({
        onSuccess: (data, variables) => {
            setLookupStarted(false);
            const s = data.suggestions;
            const committedMfg = formatPartNumber(data.manufacturerPartNumber);
            setForm((prev) => ({
                ...prev,
                manufacturerPartNumber: committedMfg,
                name: s.name ?? prev.name,
                partType: s.partType
                    ? parseInventoryPartType(s.partType)
                    : prev.partType,
                unit: coerceInventoryUnit(s.unit, allowedUnits),
                supplierId: s.supplierId ?? prev.supplierId,
                unitCost: s.averageUnitCost ?? s.unitCost ?? prev.unitCost,
                partTerminologyId:
                    variables.category === 'part' && s.partTerminologyId != null
                        ? s.partTerminologyId
                        : null,
            }));
            if (s.supplierName) {
                setSupplierSearch(s.supplierName);
            }
            const suggestionEquivalents = (s.equivalentPartNumbers ?? [])
                .map((v) => formatPartNumber(v))
                .filter((v) => v && v !== committedMfg);
            const uniqueExtras: string[] = [];
            const seenExtras = new Set<string>();
            for (const v of suggestionEquivalents) {
                if (seenExtras.has(v)) continue;
                seenExtras.add(v);
                uniqueExtras.push(v);
            }
            if (uniqueExtras.length > 0) {
                setAdditionalPartNumbers(uniqueExtras);
            }
            const nextPrefilled = new Set<PrefillKey>();
            if (s.name) nextPrefilled.add('name');
            if (s.partType) nextPrefilled.add('partType');
            if (s.unit && allowedUnits.includes(s.unit)) nextPrefilled.add('unit');
            if (s.supplierId) nextPrefilled.add('supplierId');
            if ((s.averageUnitCost ?? s.unitCost) != null) nextPrefilled.add('unitCost');
            if (uniqueExtras.length > 0) nextPrefilled.add('additionalPartNumbers');
            setPrefilled(nextPrefilled);
            setManualModeEntered(true);
            setLastCommittedMfgPartNumber(committedMfg);
            setLastCommittedCategory(variables.category);
            setPreviewMatchItemId(data.existingItemId ?? null);
            const make =
                variables.category === 'part' && s.suggestedMake?.trim()
                    ? s.suggestedMake.trim()
                    : null;
            setSuggestedMake(make);
            if (make) {
                setAutoTaggedMake(tagListHasName(tags, make) ? null : make);
                setTags((prev) => appendTagIfMissing(prev, make));
            }
        },
        onError: (error: any) => {
            if (error?.code === 'ERR_CANCELED') return;
            setLookupStarted(false);
            toast({
                title: 'Lookup failed',
                description:
                    error?.response?.data?.message ?? 'Please try again or enter details manually.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setManualModeEntered(true);
            setLastCommittedMfgPartNumber(formatPartNumber(form.manufacturerPartNumber));
            setLastCommittedCategory(form.category);
            setSuggestedMake(null);
        },
    });

    const resetDrawerState = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        setFieldErrors({});
        setManualModeEntered(false);
        setLookupStarted(false);
        setPrefilled(new Set());
        setAutoTaggedMake(null);
        setLastCommittedMfgPartNumber('');
        setLastCommittedCategory('');
        setSupplierSearch('');
        setTags([]);
        setSuggestedMake(null);
        setMakeTagModalOpen(false);
        setIsSaving(false);
        setBins([]);
        setImagePreviewUrl((prev) => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
        });
        setImageKey(undefined);
        previewMutation.reset();
        setForm(emptyAddItemForm(defaultLocationId));
        setAdditionalPartNumbers([]);
    }, [defaultLocationId, previewMutation]);

    useEffect(() => {
        if (!isOpen) return;
        queryClient.invalidateQueries({ queryKey: ['/locations/find'] });
        resetDrawerState();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when drawer opens
    }, [isOpen]);

    const handleClose = () => {
        resetDrawerState();
        onClose();
    };

    const handleImportClick = () => {
        handleClose();
        onImport?.();
    };

    /**
     * Undo what the previous lookup filled in, keeping anything the user typed
     * or edited, so a lookup for a different part never inherits stale values.
     */
    const clearStalePrefill = () => {
        if (prefilled.size > 0) {
            const empty = emptyAddItemForm();
            setForm((prev) => {
                const next = { ...prev };
                const resetKey = <K extends keyof AddItemFormState>(key: K) => {
                    next[key] = empty[key];
                };
                prefilled.forEach((key) => {
                    if (PREFILL_FORM_FIELDS.has(key as keyof AddItemFormState)) {
                        resetKey(key as keyof AddItemFormState);
                    }
                });
                return next;
            });
            if (prefilled.has('supplierId')) setSupplierSearch('');
            if (prefilled.has('additionalPartNumbers')) setAdditionalPartNumbers([]);
            setPrefilled(new Set());
        }
        if (autoTaggedMake) {
            const key = normalizeTagNameKey(autoTaggedMake);
            setTags((prev) =>
                prev.filter((t) => !(t.isNew && normalizeTagNameKey(t.name) === key)),
            );
            setAutoTaggedMake(null);
        }
        setSuggestedMake(null);
    };

    const handleLookup = () => {
        const primary = formatPartNumber(form.manufacturerPartNumber);
        if (!primary) return;
        clearStalePrefill();
        setManualModeEntered(false);
        setLookupStarted(true);
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        previewMutation.mutate({
            manufacturerPartNumber: primary,
            category: form.category,
            signal: abortRef.current.signal,
        });
    };

    const handleSkip = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        previewMutation.reset();
        setLookupStarted(false);
        setManualModeEntered(true);
        const primary = formatPartNumber(form.manufacturerPartNumber);
        if (primary !== lastCommittedMfgPartNumber || form.category !== lastCommittedCategory) {
            clearStalePrefill();
        }
        setLastCommittedMfgPartNumber(primary);
        setLastCommittedCategory(form.category);
    };

    /** Change / Edit / Fix and retry: back to Identify with the form kept. */
    const handleBackToIdentify = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        previewMutation.reset();
        setLookupStarted(false);
        setManualModeEntered(false);
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
    };

    const step: AddItemStep =
        lookupStarted || previewMutation.isPending
            ? 'lookingUp'
            : manualModeEntered
              ? 'details'
              : 'identify';

    const previewData = previewMutation.data;
    const detailsVariant: AddItemDetailsVariant = previewMatchItemId
        ? 'existing'
        : previewData?.suggestions.name
          ? 'match'
          : previewData
            ? 'noMatch'
            : 'skipped';

    const catalogAlsoMatches = useMemo(() => {
        if (!previewData) return [];
        const primary = formatPartNumber(previewData.manufacturerPartNumber);
        const seen = new Set<string>([primary]);
        const result: string[] = [];
        for (const raw of previewData.suggestions.equivalentPartNumbers ?? []) {
            const value = formatPartNumber(raw);
            if (!value || seen.has(value)) continue;
            seen.add(value);
            result.push(value);
        }
        return result;
    }, [previewData]);

    const showMotorNotice =
        previewData?.suggestions.partTerminologyId != null ||
        !!previewData?.suggestions.suggestedMake?.trim();

    const submitCreate = async (tagsToSave: Array<InventoryTag> = tags) => {
        setIsSaving(true);
        const stockQty = form.inStockQuantity;

        try {
            const resolvedTagIds: string[] = [];
            for (const t of tagsToSave) {
                if (t.isNew) {
                    const { tag } = await createTagMutation.mutateAsync({
                        name: t.name,
                    });
                    resolvedTagIds.push(tag._id);
                } else {
                    resolvedTagIds.push(t._id);
                }
            }

            const resolvedBinIds: string[] = [];
            for (const b of bins) {
                if (b.isNew) {
                    const { bin } = await createBinMutation.mutateAsync({
                        locationId: form.locationId,
                        name: b.name,
                    });
                    resolvedBinIds.push(bin._id);
                } else {
                    resolvedBinIds.push(b._id);
                }
            }

            const primary = formatPartNumber(form.manufacturerPartNumber);
            if (!primary) {
                toast({
                    title: 'Missing required fields',
                    description: 'Mfg. part number and name are required.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            const seen = new Set<string>([primary]);
            const equivalentPartNumbers: string[] = [primary];
            for (const raw of additionalPartNumbers) {
                const value = formatPartNumber(raw);
                if (!value || seen.has(value)) continue;
                seen.add(value);
                equivalentPartNumbers.push(value);
            }

            const payload: CreateInventoryItemInput = {
                locationId: form.locationId,
                manufacturerPartNumber: primary,
                equivalentPartNumbers,
                name: form.name.trim(),
                category: form.category,
                partType: form.partType,
                supplierId: form.supplierId,
                unit: form.unit,
                restockAlert: form.restockAlert,
                reorderQuantity: form.restockAlert ? (form.reorderQuantity ?? 0) : form.reorderQuantity,
                targetQuantity: form.restockAlert ? (form.targetQuantity ?? 0) : form.targetQuantity,
                initialInStockQuantity: stockQty,
                ...(form.origin.trim() ? { origin: form.origin.trim() } : {}),
                ...(stockQty > 0 ? { unitCost: form.unitCost } : {}),
                ...(form.category === 'part' && form.partTerminologyId != null
                    ? { partTerminologyId: form.partTerminologyId }
                    : {}),
                ...(imageKey ? { imageKey } : {}),
                ...(resolvedTagIds.length > 0 ? { tagIds: resolvedTagIds } : {}),
                ...(resolvedBinIds.length > 0 ? { binIds: resolvedBinIds } : {}),
            };

            const { item } = await createMutation.mutateAsync(payload);

            toast({
                title: 'Item added to inventory',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            queryClient.invalidateQueries({
                queryKey: ['/inventory/adjustments/feed', item._id],
            });
            handleClose();
        } catch {
            // create errors handled by handleCreateError
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => {
        if (!formatPartNumber(form.manufacturerPartNumber) || !form.name.trim()) {
            toast({
                title: 'Missing required fields',
                description: 'Mfg. part number and name are required.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (!form.locationId || !selectableLocations.some((l) => l._id === form.locationId)) {
            setFieldErrors((prev) => ({
                ...prev,
                locationId: form.locationId ? 'Location not found.' : 'Location is required.',
            }));
            return;
        }

        if (!form.supplierId) {
            setFieldErrors((prev) => ({
                ...prev,
                supplierId: 'Select a supplier from the list.',
            }));
            toast({
                title: 'Supplier required',
                description: 'Select a supplier from the list.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (compositeDuplicateItemId) return;

        if (form.restockAlert) {
            if (form.reorderQuantity == null || form.targetQuantity == null) {
                toast({
                    title: 'Restock quantities required',
                    description:
                        'Reorder and target quantities are required when restock alert is on.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
        }

        if (form.category !== 'part') {
            submitCreate();
            return;
        }

        const make = suggestedMake?.trim() || null;
        if (make && tagListHasName(tags, make)) {
            submitCreate();
            return;
        }
        setMakeTagModalOpen(true);
    };

    const dropPrefilled = (key: PrefillKey) => {
        setPrefilled((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
    };

    const updateField = <K extends keyof AddItemFormState>(key: K, value: AddItemFormState[K]) => {
        if (PREFILL_FORM_FIELDS.has(key) && form[key] !== value) {
            dropPrefilled(key as PrefillField);
        }
        setForm((prev) => {
            if (key === 'locationId' && prev.locationId !== value) {
                setBins([]);
            }
            return { ...prev, [key]: value };
        });
        if (key === 'locationId' || key === 'supplierId' || key === 'unit') {
            setCompositeDuplicateItemId(null);
            const formKey = key as InventoryFormReferenceField;
            setFieldErrors((prev) => {
                if (!prev[formKey]) return prev;
                const next = { ...prev };
                delete next[formKey];
                return next;
            });
        }
    };

    const handleAdditionalPartNumbersChange = (value: Array<string>) => {
        setAdditionalPartNumbers(value);
        dropPrefilled('additionalPartNumbers');
    };

    const handleRestockAlertChange = (on: boolean) => {
        setForm((prev) => ({
            ...prev,
            restockAlert: on,
            ...(on
                ? {
                      reorderQuantity: prev.reorderQuantity ?? 0,
                      targetQuantity: prev.targetQuantity ?? 0,
                  }
                : {}),
        }));
    };

    const handleMfgPartNumberChange = (value: string) => {
        const stripped = formatPartNumber(value);
        setForm((prev) => ({
            ...prev,
            manufacturerPartNumber: value,
            ...(lastCommittedMfgPartNumber !== '' && stripped !== lastCommittedMfgPartNumber
                ? { partTerminologyId: null }
                : {}),
        }));
        setPreviewMatchItemId(null);
        if (stripped) {
            setAdditionalPartNumbers((prev) =>
                prev.filter((pn) => formatPartNumber(pn) !== stripped),
            );
        }
    };

    const handleMfgPartNumberBlur = () => {
        const stripped = formatPartNumber(form.manufacturerPartNumber);
        if (stripped !== form.manufacturerPartNumber) {
            setForm((prev) => ({ ...prev, manufacturerPartNumber: stripped }));
        }
    };

    const handleCategoryChange = (value: InventoryCategory) => {
        setForm((prev) => ({
            ...prev,
            category: value,
            partTerminologyId: null,
        }));
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        if (value !== 'part') {
            setSuggestedMake(null);
        }
    };

    const missingRequiredFields = useMemo(() => {
        const missing: RequiredFieldKey[] = [];
        if (!manualModeEntered) return missing;
        if (!form.category) missing.push('category');
        if (!formatPartNumber(form.manufacturerPartNumber)) missing.push('manufacturerPartNumber');
        if (!form.name.trim()) missing.push('name');
        if (!form.partType) missing.push('partType');
        if (!form.supplierId) missing.push('supplierId');
        if (!form.locationId) missing.push('locationId');
        if (form.inStockQuantity == null || Number.isNaN(form.inStockQuantity)) {
            missing.push('inStockQuantity');
        }
        if (!form.unit) missing.push('unit');
        if (form.restockAlert) {
            if (form.reorderQuantity == null) missing.push('reorderQuantity');
            if (form.targetQuantity == null) missing.push('targetQuantity');
        }
        return missing;
    }, [manualModeEntered, form]);

    const handleRequiredFieldClick = (key: RequiredFieldKey) => {
        if (IDENTIFY_FIELDS.has(key)) {
            handleBackToIdentify();
            return;
        }
        void focusAddItemField(key, sectionsRef.current?.revealElement);
    };

    const revokeImagePreview = () => {
        setImagePreviewUrl((prev) => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const imageField = (
        <InventoryItemImageField
            fullWidth
            previewUrl={imagePreviewUrl}
            alt={form.name}
            isUploading={uploadImageMutation.isPending}
            cropTitle={`${form.name.trim() || '—'} · ${formatPartNumber(form.manufacturerPartNumber) || '—'}`}
            onFileSelected={async (file) => {
                const objectUrl = URL.createObjectURL(file);
                setImagePreviewUrl((prev) => {
                    if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                    return objectUrl;
                });
                try {
                    const key = await uploadImageMutation.mutateAsync(file);
                    setImageKey(key);
                } catch {
                    revokeImagePreview();
                    setImageKey(undefined);
                    toast({
                        title: 'Image upload failed',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                }
            }}
            onClear={() => {
                revokeImagePreview();
                setImageKey(undefined);
            }}
        />
    );

    const cancelButton = (
        <Button size="action" variant="outlineGray" onClick={handleClose}>
            Cancel
        </Button>
    );

    const footer =
        step === 'identify' ? (
            <>
                {cancelButton}
                <Button
                    size="action"
                    onClick={handleLookup}
                    isDisabled={!formatPartNumber(form.manufacturerPartNumber)}
                    rightIcon={<Icon name="arrowRight" size="sm" />}
                >
                    Look Up Part
                </Button>
            </>
        ) : step === 'lookingUp' ? (
            <>
                {cancelButton}
                <Button size="action" isDisabled>
                    Add Item
                </Button>
            </>
        ) : (
            <>
                {cancelButton}
                <Button
                    size="action"
                    onClick={handleSave}
                    isLoading={
                        isSaving ||
                        createMutation.isPending ||
                        createTagMutation.isPending ||
                        createBinMutation.isPending
                    }
                    loadingText="Adding…"
                    isDisabled={
                        isSaving ||
                        !!compositeDuplicateItemId ||
                        isUnitsLoading ||
                        isUnitsError ||
                        allowedUnits.length === 0 ||
                        uploadImageMutation.isPending
                    }
                >
                    Add Item
                </Button>
            </>
        );

    return (
        <>
        <AppDrawer
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Item"
            subtitle={
                organizationName ? `${organizationName} · Inventory` : 'Inventory'
            }
            size={layout === 'mobile' ? 'full' : 'md'}
            bodyProps={{ px: 5, py: 0 }}
            footerLeft={
                step === 'details' ? (
                    <RequiredFieldsStatus
                        missing={missingRequiredFields}
                        onFieldClick={handleRequiredFieldClick}
                    />
                ) : undefined
            }
            footer={footer}
        >
            {step === 'identify' && (
                <IdentifyStep
                    category={form.category}
                    onCategoryChange={handleCategoryChange}
                    partNumber={form.manufacturerPartNumber}
                    onPartNumberChange={handleMfgPartNumberChange}
                    onPartNumberBlur={handleMfgPartNumberBlur}
                    onLookup={handleLookup}
                    onSkip={handleSkip}
                    onImport={onImport ? handleImportClick : undefined}
                />
            )}

            {step === 'lookingUp' && (
                <LookingUpStep
                    partNumber={form.manufacturerPartNumber}
                    category={form.category}
                    onChange={handleBackToIdentify}
                    onSkip={handleSkip}
                />
            )}

            {step === 'details' && (
                <Box pt={5} pb={5}>
                    <ItemDetailsForm
                        sectionsRef={sectionsRef}
                        header={
                            <PartSummaryCard
                                variant={detailsVariant}
                                partNumber={form.manufacturerPartNumber}
                                category={form.category}
                                alsoMatches={catalogAlsoMatches}
                                showMotorNotice={showMotorNotice}
                                existingItemId={previewMatchItemId}
                                duplicateItemId={compositeDuplicateItemId}
                                onChange={handleBackToIdentify}
                                onViewItem={onViewExistingItem}
                            />
                        }
                        variant={detailsVariant}
                        form={form}
                        onFieldChange={updateField}
                        onRestockAlertChange={handleRestockAlertChange}
                        prefilled={prefilled as ReadonlySet<PrefillField>}
                        fieldErrors={fieldErrors}
                        additionalPartNumbers={additionalPartNumbers}
                        onAdditionalPartNumbersChange={handleAdditionalPartNumbersChange}
                        supplier={{
                            display: supplierDisplay,
                            options: supplierOptions,
                            onSearch: setSupplierSearch,
                            onSelect: (option) => {
                                updateField('supplierId', option.value);
                                setSupplierSearch(option.label);
                            },
                            isLoading: isSupplierLoading,
                        }}
                        locationOptions={locationOptions}
                        bins={bins}
                        onBinsChange={setBins}
                        allowedUnits={allowedUnits}
                        isUnitsLoading={isUnitsLoading}
                        isUnitsError={isUnitsError}
                        tags={tags}
                        onTagsChange={setTags}
                        suggestedMake={suggestedMake}
                        hasPhoto={!!imagePreviewUrl}
                        imageField={imageField}
                    />
                </Box>
            )}
        </AppDrawer>
        <ConfirmMakeTagModal
            isOpen={makeTagModalOpen}
            makeName={suggestedMake}
            onConfirm={(name) => {
                const next = appendTagIfMissing(tags, name);
                setTags(next);
                setMakeTagModalOpen(false);
                submitCreate(next);
            }}
            onCancel={() => setMakeTagModalOpen(false)}
        />
        </>
    );
};

export default AddInventoryItemDrawer;
